import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Check, FileText } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];
const LOCATIONS = ["Dubai Marina", "Downtown Dubai", "Dubai Creek Harbour", "Business Bay", "Palm Jumeirah", "Dubai Hills Estate", "Arabian Ranches"];

export default function BusinessRegisterPage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState(1);
  const [companyId, setCompanyId] = useState(null);
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    legal_name: "", trading_name: "", licence_number: "", issuing_authority: "Dubai DED",
    issue_date: "", expiry_date: "", emirate: "Dubai", address: "", contact_name: user?.name || "",
    business_email: "", phone: "", website: "", description: "", service_slugs: [], coverage: [],
  });

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  if (!ready) return <Spinner />;
  if (!user) { navigate("/login"); return null; }

  const toggle = (key, val) => {
    const list = form[key];
    setForm({ ...form, [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] });
  };

  const createCompany = async () => {
    if (!agree) { toast.error("Please accept the platform terms"); return; }
    if (!form.legal_name || !form.trading_name || !form.licence_number || form.service_slugs.length === 0) {
      toast.error("Fill legal name, trading name, licence number and at least one service"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/companies", form);
      setCompanyId(data.id);
      setStep(2);
      toast.success("Company created. Now upload your trade licence.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadLicence = async () => {
    if (!file) { toast.error("Choose a licence file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/companies/${companyId}/licence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(true);
      toast.success("Trade licence uploaded (stored privately)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/submit`);
      toast.success("Submitted for licence review");
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="propora-container py-10 max-w-3xl" data-testid="business-register-page">
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Register your business</h1>
      <p className="text-[#4A5568] mt-2">List your company on Real&State. A valid trade licence and admin approval are required before you can respond to jobs. Your licence document is stored privately and never shown to customers.</p>

      <div className="mt-8 bg-white rounded-3xl border border-[#E3E8E8] p-7 shadow-soft">
        {step === 1 && (
          <div className="space-y-5" data-testid="business-step-1">
            <h2 className="font-display font-bold text-xl text-[#0B1733]">Company & licence details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Legal name *</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="biz-legal-name" className="mt-1.5" /></div>
              <div><Label>Trading name *</Label><Input value={form.trading_name} onChange={(e) => setForm({ ...form, trading_name: e.target.value })} data-testid="biz-trading-name" className="mt-1.5" /></div>
              <div><Label>Trade licence number *</Label><Input value={form.licence_number} onChange={(e) => setForm({ ...form, licence_number: e.target.value })} data-testid="biz-licence-number" className="mt-1.5" /></div>
              <div><Label>Issuing authority</Label><Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} data-testid="biz-authority" className="mt-1.5" /></div>
              <div><Label>Issue date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="biz-issue-date" className="mt-1.5" /></div>
              <div><Label>Expiry date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="biz-expiry-date" className="mt-1.5" /></div>
              <div><Label>Emirate</Label>
                <Select value={form.emirate} onValueChange={(v) => setForm({ ...form, emirate: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="biz-emirate"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMIRATES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="biz-contact-name" className="mt-1.5" /></div>
              <div><Label>Business email</Label><Input type="email" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} data-testid="biz-email" className="mt-1.5" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="biz-phone" className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="biz-website" className="mt-1.5" /></div>
            </div>
            <div><Label>Company description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="biz-description" className="mt-1.5" /></div>
            <div>
              <Label>Services offered *</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button key={c.slug} type="button" onClick={() => toggle("service_slugs", c.slug)} data-testid={`biz-service-${c.slug}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${form.service_slugs.includes(c.slug) ? "bg-[#007F83] text-white border-[#007F83]" : "border-[#E3E8E8] text-[#0B1733] hover:bg-[#E8F5F1]"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Coverage areas</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LOCATIONS.map((l) => (
                  <button key={l} type="button" onClick={() => toggle("coverage", l)} data-testid={`biz-coverage-${l.replace(/\s/g, "-")}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${form.coverage.includes(l) ? "bg-[#0B1733] text-white border-[#0B1733]" : "border-[#E3E8E8] text-[#0B1733] hover:bg-[#E8F5F1]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-[#4A5568]">
              <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="biz-terms" className="mt-0.5" />
              <span>I confirm the details are accurate and accept the platform terms and privacy policy.</span>
            </label>
            <Button onClick={createCompany} disabled={busy} data-testid="biz-continue-btn" className="w-full rounded-full bg-[#007F83] hover:bg-[#006B70] text-white h-11">{busy ? "Saving..." : "Continue to licence upload"}</Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="business-step-2">
            <h2 className="font-display font-bold text-xl text-[#0B1733]">Upload trade licence</h2>
            <p className="text-sm text-[#4A5568]">PDF, JPG or PNG up to 10 MB. Stored privately — only you and authorized admins can access it. Never shown to customers.</p>
            <label className="block border-2 border-dashed border-[#E3E8E8] rounded-2xl p-8 text-center cursor-pointer hover:border-[#007F83] transition-colors" data-testid="licence-dropzone">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setUploaded(false); }} data-testid="licence-file-input" />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-[#0B1733]"><FileText size={20} /> {file.name} {uploaded && <Check className="text-green-600" size={18} />}</div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#4A5568]"><Upload size={28} /> Click to choose your trade licence</div>
              )}
            </label>
            <div className="flex gap-3">
              <Button onClick={uploadLicence} disabled={busy || !file} variant="outline" data-testid="licence-upload-btn" className="rounded-full">{busy ? "Uploading..." : uploaded ? "Re-upload" : "Upload licence"}</Button>
              <Button onClick={submit} disabled={busy || !uploaded} data-testid="biz-submit-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white flex-1">Submit for review</Button>
            </div>
            <p className="text-xs text-[#4A5568]">A document upload is not verification. An admin reviews the licence before your listing goes public.</p>
          </div>
        )}
      </div>
    </div>
  );
}
